export const SYMBOL_IDS = [
  'BEWD',
  'DM',
  'JINZO',
  'TW',
  'ACE',
  'KING',
  'QUEEN',
  'JACK',
  'WILD',
  'SCATTER',
] as const;

export type SymbolId = (typeof SYMBOL_IDS)[number];
export type PresentationMode = 'cards' | 'performance';

export interface GridPosition {
  readonly reel: number;
  readonly row: number;
  readonly multiplier?: number;
}

export interface GodPresentationEvent {
  readonly god: 'RA' | 'SLIFER' | 'OBELISK';
  readonly order: number;
  readonly sliferTargets?: readonly (GridPosition & { readonly previousMultiplier: number; readonly multiplier: number })[];
  readonly raAwardedSpins?: 2 | 3;
  readonly raBoostBefore?: number;
  readonly raBoostAfter?: number;
  readonly raFullGrid?: boolean;
  readonly obeliskMode?: 'wild-reel' | 'full-grid-multiplier';
  readonly obeliskCandidates?: readonly {
    readonly reel: number;
    readonly cells?: readonly GridPosition[];
    readonly winCents: number;
    readonly winningLines: number;
  }[];
  readonly obeliskReel?: number;
  readonly obeliskLevel?: number;
  readonly obeliskCells?: readonly GridPosition[];
  readonly obeliskTransformsScatters?: boolean;
  readonly gridBefore?: SlotGrid;
  readonly gridAfter?: SlotGrid;
}

export type ReelWindow = readonly [SymbolId, SymbolId, SymbolId];
export type SlotGrid = readonly [ReelWindow, ReelWindow, ReelWindow, ReelWindow, ReelWindow];
export type ReelStops = readonly [number, number, number, number, number];
export type PaylineRoute = readonly [number, number, number, number, number];

export interface PresentedLineWin {
  readonly lineIndex: number;
  readonly route: PaylineRoute;
  readonly count: 3 | 4 | 5;
  readonly symbolId: Exclude<SymbolId, 'SCATTER'>;
  readonly winCents: number;
}

export interface PotOfGreedPresentation {
  readonly position: GridPosition;
  readonly awardedFreeSpins: 1;
  readonly transformedToWild: true;
}

/**
 * Immutable display DTO. The renderer never creates, rerolls or evaluates it.
 */
export interface SpinPresentationResult {
  readonly grid: SlotGrid;
  readonly stops: ReelStops;
  readonly scatterCount: number;
  readonly lineWins: readonly PresentedLineWin[];
  readonly totalWinCents: number;
  readonly stickyBefore: readonly GridPosition[];
  readonly stickyAfter: readonly GridPosition[];
  readonly newlySticky: readonly GridPosition[];
  readonly retriggerAwarded?: number;
  readonly potOfGreed?: PotOfGreedPresentation;
  readonly godEvents?: readonly GodPresentationEvent[];
}

export interface ReplayPresentationStep extends SpinPresentationResult {
  readonly spinIndex: number;
  readonly cumulativeWinCents: number;
  readonly freeSpinsLeftAfter: number;
  readonly freeSpinsTotalAfter: number;
}

export function gridPositionKey(position: GridPosition): string {
  return `${position.reel}:${position.row}`;
}

export function isGridPosition(position: GridPosition): boolean {
  return Number.isInteger(position.reel)
    && position.reel >= 0
    && position.reel < 5
    && Number.isInteger(position.row)
    && position.row >= 0
    && position.row < 3;
}
